import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  Lock,
  Download,
  RefreshCw,
  Play,
  UserCheck,
  FileText,
  ArrowRight,
  Zap,
  BarChart3,
  Undo2,
  MessageSquare,
  X,
  Send,
  ChevronDown,
  ChevronUp,
  Filter,
  ArrowUpDown,
  Link2,
  CalendarClock,
} from 'lucide-react';
import { TASK_STATUS } from '../../workflow/taskModel';
import {
  onAdmissionConfirmed,
  getOnboardingDashboard,
  checkSettlingTransitions,
} from '../../services/workflow/WorkflowOrchestrator';
import {
  completeTask,
  setTaskInProgress,
  escalateOverdueTasks,
  reopenTask,
  addTaskNote,
} from '../../services/workflow/TaskService';
import DocumentEngine from '../../services/documents/DocumentEngine';
import DocumentManager from '../Documents/DocumentManager';
import { Tooltip } from '../Common/ReusableUI';
import { useToast } from '../Common/ToastProvider';

const STATUS_CONFIG = {
  [TASK_STATUS.COMPLETED]: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Completed' },
  [TASK_STATUS.IN_PROGRESS]: { icon: Play, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'In Progress' },
  [TASK_STATUS.PENDING]: { icon: Circle, color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Pending' },
  [TASK_STATUS.OVERDUE]: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Overdue' },
  [TASK_STATUS.BLOCKED]: { icon: Lock, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Blocked' },
  [TASK_STATUS.CANCELLED]: { icon: Circle, color: 'text-slate-300', bg: 'bg-slate-50', border: 'border-slate-100', label: 'Cancelled' },
};

const STATUS_TOOLTIPS = {
  [TASK_STATUS.COMPLETED]: 'Completed and verified — no further action required',
  [TASK_STATUS.IN_PROGRESS]: 'Actively being worked on by the assigned owner',
  [TASK_STATUS.PENDING]: 'Waiting to be started — no action taken yet',
  [TASK_STATUS.OVERDUE]: 'Past its due date — needs urgent attention',
  [TASK_STATUS.BLOCKED]: 'Cannot proceed until prerequisite tasks are resolved',
  [TASK_STATUS.CANCELLED]: 'Cancelled and excluded from progress tracking',
};

const DAYS_PER_TASK = 2;

function formatEstDate(date) {
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function computeEstimatedDates(tasks) {
  const incompleteTasks = tasks.filter(
    (t) => t.status !== TASK_STATUS.COMPLETED && t.status !== TASK_STATUS.CANCELLED
  );
  const map = {};
  incompleteTasks.forEach((t, idx) => {
    const est = new Date();
    est.setDate(est.getDate() + (idx + 1) * DAYS_PER_TASK);
    map[t.id] = est;
  });
  return map;
}

function getOverallEstimatedCompletion(tasks) {
  const incomplete = tasks.filter(
    (t) => t.status !== TASK_STATUS.COMPLETED && t.status !== TASK_STATUS.CANCELLED
  );
  if (incomplete.length === 0) return null;
  const est = new Date();
  est.setDate(est.getDate() + incomplete.length * DAYS_PER_TASK);
  return est;
}

const SORT_OPTIONS = [
  { value: 'default', label: 'Default Order' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
];

const PRIORITY_WEIGHT = { critical: 0, high: 1, normal: 2, low: 3 };
const STATUS_WEIGHT = {
  [TASK_STATUS.OVERDUE]: 0,
  [TASK_STATUS.BLOCKED]: 1,
  [TASK_STATUS.IN_PROGRESS]: 2,
  [TASK_STATUS.PENDING]: 3,
  [TASK_STATUS.COMPLETED]: 4,
  [TASK_STATUS.CANCELLED]: 5,
};

function ProgressBar({ percent, label, size = 'md' }) {
  const height = size === 'lg' ? 'h-3' : 'h-2';
  const barColor = percent >= 100 ? 'bg-emerald-500' : percent >= 60 ? 'bg-purple-500' : percent >= 30 ? 'bg-blue-500' : 'bg-slate-400';

  return (
    <div>
      {label && (
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600">{label}</span>
          <span className="text-xs font-bold text-slate-800">{percent}%</span>
        </div>
      )}
      <div className={`${height} w-full overflow-hidden rounded-full bg-slate-200`}>
        <div
          className={`${height} rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Confirmation dialog — blocks destructive actions until the user confirms.
 */
function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmColor = 'bg-purple-600 hover:bg-purple-700', onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div
        className="mx-4 w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="text-sm font-bold text-slate-900">{title}</h4>
        <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{message}</p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-4 py-1.5 text-xs font-semibold text-white shadow-sm ${confirmColor}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A single task row with evidence notes, undo, and inline comment support.
 */
function TaskItem({ task, onComplete, onStartProgress, onReopen, onAddNote, residentId, blockedByTasks = [], unlocksTasks = [], estimatedCompletion }) {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isActionable = task.status === TASK_STATUS.PENDING || task.status === TASK_STATUS.IN_PROGRESS || task.status === TASK_STATUS.OVERDUE;
  const isCompleted = task.status === TASK_STATUS.COMPLETED;
  const isCancelled = task.status === TASK_STATUS.CANCELLED;

  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [evidenceNote, setEvidenceNote] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleSubmitComplete = () => {
    onComplete(task.id, evidenceNote.trim());
    setShowCompleteForm(false);
    setEvidenceNote('');
  };

  const handleSubmitNote = () => {
    if (!noteText.trim()) return;
    onAddNote(task.id, noteText.trim());
    setShowNoteForm(false);
    setNoteText('');
  };

  const handleConfirmReopen = () => {
    onReopen(task.id, reopenReason.trim());
    setShowReopenConfirm(false);
    setReopenReason('');
  };

  return (
    <div className={`group rounded-lg border ${cfg.border} ${cfg.bg} p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <Tooltip content={STATUS_TOOLTIPS[task.status] || cfg.label} position="right">
            <Icon className={`h-5 w-5 ${cfg.color}`} />
          </Tooltip>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium ${isCompleted ? 'line-through text-slate-500' : 'text-slate-900'}`}>
              {task.title}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              {task.priority === 'critical' && (
                <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                  CRITICAL
                </span>
              )}
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="rounded-md p-0.5 text-slate-400 hover:text-slate-600"
                title={expanded ? 'Collapse' : 'Expand details'}
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          {task.description && (
            <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{task.description}</p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
            {task.ownerRole && (
              <Tooltip content={`Assigned to the ${task.ownerRole} team`} position="top">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-slate-600 border border-slate-200">
                  <UserCheck className="h-2.5 w-2.5" />
                  {task.ownerRole}
                </span>
              </Tooltip>
            )}
            {task.dueAt && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${task.status === TASK_STATUS.OVERDUE ? 'bg-red-100 text-red-700 font-semibold' : 'bg-white/60 text-slate-500 border border-slate-200'}`}>
                <Clock className="h-2.5 w-2.5" />
                Due: {task.dueAt}
              </span>
            )}
            {task.completedBy && (
              <span className="text-emerald-600">
                ✓ by {task.completedBy} {task.completedAt ? `on ${new Date(task.completedAt).toLocaleDateString()}` : ''}
              </span>
            )}
            {task.blockedReason && (
              <span className="text-amber-700 font-medium">{task.blockedReason}</span>
            )}
            {blockedByTasks.length > 0 && (
              <Tooltip content={`Waiting on: ${blockedByTasks.map(t => t.title).join(', ')}`} position="top">
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700 border border-amber-200 font-medium cursor-help">
                  <Lock className="h-2.5 w-2.5" />
                  Blocked by: {blockedByTasks.map(t => t.title).join(', ')}
                </span>
              </Tooltip>
            )}
            {unlocksTasks.length > 0 && (
              <Tooltip content={`Completing this will unblock: ${unlocksTasks.map(t => t.title).join(', ')}`} position="top">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-blue-700 border border-blue-200 cursor-help">
                  <Link2 className="h-2.5 w-2.5" />
                  Unlocks: {unlocksTasks.length} task{unlocksTasks.length !== 1 ? 's' : ''}
                </span>
              </Tooltip>
            )}
            {estimatedCompletion && !isCompleted && !isCancelled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-purple-700 border border-purple-200">
                <CalendarClock className="h-2.5 w-2.5" />
                Est. completion: {formatEstDate(estimatedCompletion)}
              </span>
            )}
          </div>

          {/* Expanded details: notes history + reopen history */}
          {expanded && (
            <div className="mt-2 space-y-1.5">
              {task.evidenceNote && (
                <div className="rounded-md bg-white/80 border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600">
                  <p className="font-semibold text-slate-700 mb-0.5">Notes</p>
                  <p className="whitespace-pre-line">{task.evidenceNote}</p>
                </div>
              )}
              {task._reopenHistory?.length > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-700">
                  <p className="font-semibold mb-0.5">Reopen history</p>
                  {task._reopenHistory.map((h, i) => (
                    <p key={i}>
                      {new Date(h.reopenedAt).toLocaleString()} — reopened by {h.reopenedBy}
                      {h.reason ? `: ${h.reason}` : ''}
                      {h.previousCompletedBy ? ` (was completed by ${h.previousCompletedBy})` : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex shrink-0 flex-col items-center gap-1">
          {isActionable && !showCompleteForm && (
            <>
              {task.status === TASK_STATUS.PENDING && (
                <button
                  type="button"
                  onClick={() => onStartProgress(task.id)}
                  className="rounded-md bg-blue-50 p-1.5 text-blue-600 hover:bg-blue-100"
                  title="Start working on this"
                >
                  <Play className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCompleteForm(true)}
                className="rounded-md bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100"
                title="Complete with note"
              >
                <CheckCircle className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {(isCompleted || isCancelled) && (
            <button
              type="button"
              onClick={() => setShowReopenConfirm(true)}
              className="rounded-md bg-amber-50 p-1.5 text-amber-600 hover:bg-amber-100"
              title="Undo — reopen this task"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          )}
          {!showNoteForm && (
            <button
              type="button"
              onClick={() => setShowNoteForm(true)}
              className="rounded-md bg-slate-50 p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Add note"
            >
              <MessageSquare className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Complete with evidence note form */}
      {showCompleteForm && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-700 mb-1.5">Mark complete — add evidence note (optional)</p>
          <textarea
            value={evidenceNote}
            onChange={(e) => setEvidenceNote(e.target.value)}
            placeholder="What was done? Who was present? Any follow-up needed?"
            rows={2}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-100 resize-none"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowCompleteForm(false); setEvidenceNote(''); }}
              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitComplete}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
            >
              <CheckCircle className="h-3 w-3" />
              Complete
            </button>
          </div>
        </div>
      )}

      {/* Add note form (without completing) */}
      {showNoteForm && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-700 mb-1.5">Add a note</p>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Progress update, concern, or follow-up note…"
            rows={2}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder-slate-400 focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-100 resize-none"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowNoteForm(false); setNoteText(''); }}
              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitNote}
              disabled={!noteText.trim()}
              className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-3 w-3" />
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* Reopen confirmation */}
      {showReopenConfirm && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-800 mb-1.5">Reopen this task?</p>
          <p className="text-xs text-amber-700 mb-2">
            This will undo the completion and set the task back to pending. The original completion record is preserved in the audit trail.
          </p>
          <textarea
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            placeholder="Reason for reopening (optional but recommended)…"
            rows={2}
            className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder-amber-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100 resize-none"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowReopenConfirm(false); setReopenReason(''); }}
              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmReopen}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-amber-700"
            >
              <Undo2 className="h-3 w-3" />
              Reopen Task
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnboardingWorkflow({ residentId, facilityId, admissionDateIso, residentData = {} }) {
  const [dashboard, setDashboard] = useState(null);
  const [activeSection, setActiveSection] = useState('stage6');
  const [showDocuments, setShowDocuments] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerRoleFilter, setOwnerRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const { showToast } = useToast();

  const refresh = useCallback(() => {
    const data = getOnboardingDashboard(residentId, admissionDateIso);
    setDashboard(data);
  }, [residentId, admissionDateIso]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleActivateWorkflow = useCallback(() => {
    setConfirmAction({
      title: 'Activate Admission Workflow?',
      message: 'This will create all Stage 6 (Admission) and Stage 7A (Settling In) tasks, generate the admission document pack, and notify the team. This cannot be undone.',
      confirmLabel: 'Activate Workflow',
      onConfirm: () => {
        onAdmissionConfirmed({
          residentId,
          facilityId,
          admissionDateIso: admissionDateIso || new Date().toISOString().split('T')[0],
          residentData,
          createdBy: 'current_user',
        });
        refresh();
        setConfirmAction(null);
        showToast('Admission workflow activated — tasks created and team notified', 'success');
      },
    });
  }, [residentId, facilityId, admissionDateIso, residentData, refresh, showToast]);

  const handleCompleteTask = useCallback((taskId, note) => {
    completeTask(residentId, taskId, 'current_user', note || undefined);
    refresh();
    showToast('Task marked as completed', 'success');
  }, [residentId, refresh, showToast]);

  const handleStartProgress = useCallback((taskId) => {
    setTaskInProgress(residentId, taskId);
    refresh();
    showToast('Task started — now in progress', 'info');
  }, [residentId, refresh, showToast]);

  const handleReopenTask = useCallback((taskId, reason) => {
    reopenTask(residentId, taskId, 'current_user', reason);
    refresh();
  }, [residentId, refresh]);

  const handleAddNote = useCallback((taskId, note) => {
    addTaskNote(residentId, taskId, note, 'current_user');
    refresh();
    showToast('Note added to task', 'success');
  }, [residentId, refresh, showToast]);

  const handleEscalate = useCallback(() => {
    setConfirmAction({
      title: 'Escalate all overdue tasks?',
      message: 'All overdue tasks will be reassigned to Facility Manager with critical priority. This is recorded in the audit trail.',
      confirmLabel: 'Escalate All',
      confirmColor: 'bg-red-600 hover:bg-red-700',
      onConfirm: () => {
        escalateOverdueTasks(residentId, 'current_user');
        refresh();
        setConfirmAction(null);
        showToast('All overdue tasks escalated to Facility Manager', 'warning');
      },
    });
  }, [residentId, refresh, showToast]);

  const handleExportChecklist = useCallback(() => {
    if (!dashboard) return;
    const tasks = [...dashboard.stage6.tasks, ...dashboard.stage7a.tasks];
    const html = DocumentEngine.exportAdmissionChecklistHTML(residentId, tasks, residentData);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }, [dashboard, residentId, residentData]);

  const handleCheckTransition = useCallback(() => {
    const residents = [{ id: residentId, admissionDate: admissionDateIso, name: residentData.residentName }];
    checkSettlingTransitions(facilityId, residents);
    refresh();
  }, [residentId, facilityId, admissionDateIso, residentData, refresh]);

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
      </div>
    );
  }

  const hasWorkflow = dashboard.stage6.tasks.length > 0 || dashboard.stage7a.tasks.length > 0;

  if (!hasWorkflow) {
    return (
      <>
        {confirmAction && (
          <ConfirmDialog
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            confirmColor={confirmAction.confirmColor}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
        <div className="rounded-xl border border-dashed border-purple-300 bg-purple-50/50 p-8 text-center">
          <Zap className="mx-auto h-12 w-12 text-purple-400 mb-3" />
          <h3 className="text-lg font-bold text-slate-900 mb-1">Activate Onboarding Workflow</h3>
          <p className="text-sm text-slate-600 mb-4 max-w-md mx-auto">
            This will create the Stage 6 (Admission) and Stage 7A (Settling In) task checklists,
            generate all admission documents, and notify the team.
          </p>
          <button
            type="button"
            onClick={handleActivateWorkflow}
            className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-purple-700 transition-colors"
          >
            <Play className="h-5 w-5" />
            Activate Admission Workflow
          </button>
        </div>
      </>
    );
  }

  const { stage6, stage7a, settling, totalProgress, overdueTasks, blockedTasks, pendingDocuments } = dashboard;
  const sections = [
    { id: 'stage6', label: 'Stage 6: Admission', tasks: stage6.tasks, progress: stage6.progress },
    { id: 'stage7a', label: 'Stage 7A: Settling In', tasks: stage7a.tasks, progress: stage7a.progress },
  ];
  const active = sections.find((s) => s.id === activeSection) || sections[0];
  const allTasks = [...stage6.tasks, ...stage7a.tasks];

  const templateIdToTask = {};
  allTasks.forEach((t) => { if (t.templateId) templateIdToTask[t.templateId] = t; });
  const blockedByMap = {};
  const unblocksMap = {};
  allTasks.forEach((t) => {
    if (t.dependsOnTemplateIds?.length) {
      blockedByMap[t.id] = t.dependsOnTemplateIds.map((tid) => templateIdToTask[tid]).filter(Boolean);
      t.dependsOnTemplateIds.forEach((tid) => {
        const dep = templateIdToTask[tid];
        if (dep) {
          if (!unblocksMap[dep.id]) unblocksMap[dep.id] = [];
          unblocksMap[dep.id].push(t);
        }
      });
    }
  });
  const estimatedDates = computeEstimatedDates(allTasks);
  const overallEstDate = getOverallEstimatedCompletion(allTasks);
  const ownerRoles = [...new Set(allTasks.map((t) => t.ownerRole).filter(Boolean))].sort();

  let filteredTasks = [...active.tasks];
  if (statusFilter !== 'all') filteredTasks = filteredTasks.filter((t) => t.status === statusFilter);
  if (ownerRoleFilter !== 'all') filteredTasks = filteredTasks.filter((t) => t.ownerRole === ownerRoleFilter);
  if (sortBy === 'dueDate') filteredTasks.sort((a, b) => (a.dueAt || '9999').localeCompare(b.dueAt || '9999'));
  else if (sortBy === 'priority') filteredTasks.sort((a, b) => (PRIORITY_WEIGHT[a.priority] ?? 2) - (PRIORITY_WEIGHT[b.priority] ?? 2));
  else if (sortBy === 'status') filteredTasks.sort((a, b) => (STATUS_WEIGHT[a.status] ?? 3) - (STATUS_WEIGHT[b.status] ?? 3));

  return (
    <div className="space-y-4">
      {/* Confirmation overlay */}
      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          confirmColor={confirmAction.confirmColor}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Summary Dashboard */}
      <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            <h3 className="text-base font-bold text-slate-900">Onboarding Progress</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportChecklist}
              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50"
            >
              <Download className="h-3.5 w-3.5" />
              Export PDF
            </button>
            <button
              type="button"
              onClick={handleCheckTransition}
              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50"
              title="Check if settling-in is complete and auto-transition to 7B"
            >
              <ArrowRight className="h-3.5 w-3.5" />
              Check Transition
            </button>
            <button
              type="button"
              onClick={refresh}
              className="rounded-lg border border-purple-200 bg-white p-1.5 text-purple-600 hover:bg-purple-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg bg-white p-3 shadow-sm border border-slate-100">
            <Tooltip content={`${totalProgress.completed} of ${totalProgress.total} tasks completed across all stages`} position="bottom">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Progress</p>
              <p className="text-2xl font-bold text-purple-700">{totalProgress.percent}%</p>
              <p className="text-xs text-slate-500">{totalProgress.completed}/{totalProgress.total} tasks</p>
            </Tooltip>
            {overallEstDate && totalProgress.percent < 100 && (
              <p className="mt-1 text-[10px] font-medium text-purple-600 flex items-center gap-1">
                <CalendarClock className="h-2.5 w-2.5" />
                Est. complete by: {formatEstDate(overallEstDate)}
              </p>
            )}
          </div>
          <div className="rounded-lg bg-white p-3 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Overdue</p>
            <p className={`text-2xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {overdueTasks.length}
            </p>
            {overdueTasks.length > 0 && (
              <button
                type="button"
                onClick={handleEscalate}
                className="text-[10px] font-medium text-red-600 hover:text-red-800 underline"
              >
                Escalate all
              </button>
            )}
          </div>
          <div className="rounded-lg bg-white p-3 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Blocked</p>
            <p className={`text-2xl font-bold ${blockedTasks.length > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
              {blockedTasks.length}
            </p>
          </div>
          <div className="rounded-lg bg-white p-3 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Documents</p>
            <p className="text-2xl font-bold text-blue-600">{pendingDocuments.length}</p>
            <p className="text-xs text-slate-500">pending signature</p>
          </div>
        </div>

        {/* Progress bars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Tooltip content={`Admission: ${stage6.progress.completed}/${stage6.progress.total} tasks done`} position="bottom">
            <ProgressBar percent={stage6.progress.percent} label="Stage 6: Admission" size="lg" />
          </Tooltip>
          <Tooltip content={`Settling In: ${stage7a.progress.completed}/${stage7a.progress.total} tasks done`} position="bottom">
            <ProgressBar percent={stage7a.progress.percent} label="Stage 7A: Settling In" size="lg" />
          </Tooltip>
        </div>

        {/* Settling transition info */}
        {settling && (
          <Tooltip
            content={settling.ready
              ? 'All settling-in criteria met — resident can transition to Stage 7B (Ongoing Care)'
              : `The settling-in period lasts 28 days from admission. ${28 - (settling.daysSinceAdmission || 0)} days remaining.`
            }
            position="bottom"
          >
            <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${
              settling.ready
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-blue-50 border border-blue-200 text-blue-700'
            }`}>
              {settling.ready
                ? `Ready to transition to Stage 7B — ${settling.reason === 'all_tasks_completed' ? 'all tasks completed' : 'day 28 reached'}`
                : `Day ${settling.daysSinceAdmission}/28 — ${settling.pendingTasks?.length || 0} tasks remaining`
              }
            </div>
          </Tooltip>
        )}
      </div>

      {/* Section Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { setActiveSection(s.id); setShowDocuments(false); }}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              activeSection === s.id && !showDocuments
                ? 'text-purple-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {s.label}
            <span className="ml-1.5 text-xs text-slate-400">
              ({s.progress.completed}/{s.progress.total})
            </span>
            {activeSection === s.id && !showDocuments && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-t" />
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowDocuments(!showDocuments)}
          className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
            showDocuments ? 'text-purple-700' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <span className="flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            Documents
          </span>
          {showDocuments && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-t" />
          )}
        </button>
      </div>

      {/* Filter & Sort Controls */}
      {!showDocuments && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-200"
          >
            <option value="all">All Statuses</option>
            <option value={TASK_STATUS.PENDING}>Pending</option>
            <option value={TASK_STATUS.IN_PROGRESS}>In Progress</option>
            <option value={TASK_STATUS.OVERDUE}>Overdue</option>
            <option value={TASK_STATUS.BLOCKED}>Blocked</option>
            <option value={TASK_STATUS.COMPLETED}>Completed</option>
            <option value={TASK_STATUS.CANCELLED}>Cancelled</option>
          </select>
          <select
            value={ownerRoleFilter}
            onChange={(e) => setOwnerRoleFilter(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-200"
          >
            <option value="all">All Roles</option>
            {ownerRoles.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-1.5">
            <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-200"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {(statusFilter !== 'all' || ownerRoleFilter !== 'all' || sortBy !== 'default') && (
            <button
              type="button"
              onClick={() => { setStatusFilter('all'); setOwnerRoleFilter('all'); setSortBy('default'); }}
              className="rounded-md px-2 py-1 text-[10px] font-medium text-purple-600 hover:bg-purple-100"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Task List */}
      {!showDocuments && (
        <div className="space-y-2">
          {filteredTasks.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              {active.tasks.length === 0 ? 'No tasks for this stage yet.' : 'No tasks match the current filters.'}
            </p>
          ) : (
            filteredTasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                residentId={residentId}
                onComplete={handleCompleteTask}
                onStartProgress={handleStartProgress}
                onReopen={handleReopenTask}
                onAddNote={handleAddNote}
                blockedByTasks={blockedByMap[task.id] || []}
                unlocksTasks={unblocksMap[task.id] || []}
                estimatedCompletion={estimatedDates[task.id]}
              />
            ))
          )}
        </div>
      )}

      {/* Documents Section */}
      {showDocuments && (
        <DocumentManager
          residentId={residentId}
          facilityId={facilityId}
          residentData={residentData}
        />
      )}
    </div>
  );
}
